import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const REFRESH_STORAGE_KEY = 'vh-refresh-token';

/**
 * Holds the access token in memory and the refresh token in localStorage.
 *
 * Security model:
 *  - Access token is kept in memory only (cleared on reload).
 *  - Refresh token lives in localStorage so silent refresh can run after a
 *    page reload. This matches the API contract (body-based /refresh).
 *    If httpOnly cookie refresh is later supported by the backend, drop
 *    the refresh-token storage and rely on the cookie.
 */
@Injectable({ providedIn: 'root' })
export class TokenStore {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly _accessToken = signal<string | null>(null);
  private readonly _expiresAt = signal<number | null>(null); // epoch ms
  private readonly _refreshToken = signal<string | null>(this.readStoredRefresh());

  readonly accessToken = this._accessToken.asReadonly();
  readonly expiresAt = this._expiresAt.asReadonly();
  readonly refreshToken = this._refreshToken.asReadonly();

  readonly isAuthenticated = computed(() => {
    const token = this._accessToken();
    const exp = this._expiresAt();
    return !!token && (exp === null || exp > Date.now());
  });

  readonly hasRefreshToken = computed(() => !!this._refreshToken());

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      const rt = this._refreshToken();
      try {
        if (rt) localStorage.setItem(REFRESH_STORAGE_KEY, rt);
        else localStorage.removeItem(REFRESH_STORAGE_KEY);
      } catch {
        /* ignore private mode / storage full */
      }
    });
  }

  /** Store a fresh access token (and optionally a new refresh token). */
  set(accessToken: string, expiresInSeconds: number, refreshToken?: string | null): void {
    this._accessToken.set(accessToken);
    this._expiresAt.set(Date.now() + expiresInSeconds * 1000);
    if (refreshToken !== undefined) {
      this._refreshToken.set(refreshToken);
    }
  }

  /** Replace only the refresh token (used during rotation). */
  setRefreshToken(token: string | null): void {
    this._refreshToken.set(token);
  }

  /** Wipe everything (logout / refresh failure). */
  clear(): void {
    this._accessToken.set(null);
    this._expiresAt.set(null);
    this._refreshToken.set(null);
  }

  private readStoredRefresh(): string | null {
    if (!this.isBrowser) return null;
    try {
      return localStorage.getItem(REFRESH_STORAGE_KEY);
    } catch {
      return null;
    }
  }
}
